import { useCallback, useEffect, useMemo, useState } from 'react';
import './EmployeePage.css';
import { useSelector } from 'react-redux';
import { useGetEmployeeSessionMutation, useListEmployeeMutation } from './api/employeeApi';
import Toast from '../../components/ToastNotification';
import ReactTable from '../../components/ReactTable';
import PencilIcon from "../../assets/icons/Pencil";
import TrashIcon from "../../assets/icons/Trash";
import EyeIcon from "../../assets/icons/Eye";
import ConnectIcon from "../../assets/icons/Connect";
import { AddNewEmployee, DeleteEmployee, EditEmployee, ViewEmployee, ConnectEmployee } from './components/index';
import { DEFAULT_PAGE_SIZE } from '../../utils/contants';
import Loader from '../../components/Loader';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import { encryptUrlSafe, formatePhoneNumber, redirectInNewTab } from '../../utils/helpers';

const EmployeePage = () => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [getSessionApi] = useGetEmployeeSessionMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const socket = useSocket();

  const connectWhatsapp = useCallback(async (row) => {
    const employee_id = row.original?.id;
    if (!employee_id) return Toast.error('Invalid employee');

    const is_active = await getEmployeeSession(employee_id);
    if (is_active !== row.original.is_active) {
      setEmployeeList((prev) => 
        prev.map((emp) =>
          emp.id === employee_id
            ? { ...emp, is_active }
            : emp
          )
      )
      return;
    }

    if (!socket?.connected) return Toast.error('Socket server not connected.');

    setShowLoader(true);
    socket.emit('join-employee-room',  { employeeId: employee_id});
    socket.emit('connect-whatsapp',  { employeeId: employee_id});
  }, [socket])

  const columns = useMemo(() => [
    { header: 'NAME', accessorKey: 'name' },
    { 
      header: 'PHONE', 
      cell: ({ row }) => (
        <span className="primary-text">
          {/* {row.original.country_code} {row.original.phone_number} */}
          {formatePhoneNumber(`${row.original.country_code}${row.original.phone_number}`)}
        </span>
      )
    },
    { 
      header: 'STATUS', 
      cell: ({ row }) => (
        <span style={{ color: row.original.is_active ? 'green' : 'red' }}>
          {row.original.is_active ? 'CONNECTED' : 'DISCONNECTED'}
        </span>
      )
    },
    {
      header: 'PENDING REPLY', accessorKey: 'unreplied_count'
    },
    {
      // header: 'ACRT', accessorKey: 'acrt'
      header: (
        <div className="tooltip">
          <span>ACRT</span>
          <img className='i-icon' src="/letter-i.png" alt="" />
          <span className="tooltiptext">Average Contact Response Time [m] - 30days</span>
        </div>
      ),
      accessorKey: 'acrt'
    },
    {
      // header: 'AMRT', accessorKey: 'amrt'
      header: (
        <div className="tooltip">
          <span>AMRT</span>
          <img className='i-icon' src="/letter-i.png" alt="" />
          <span className="tooltiptext">Average Message Response Time [m] - 30days</span>
        </div>
      ),
      accessorKey: 'amrt'
    }
  ], []);

  const actionMenu = useMemo(() => [
    { action: () => setViewModal(true), icon: <EyeIcon size={20} /> },
    { action: () => setEditModal(true), icon: <PencilIcon size={20} /> },
    { 
      action: (row) => {
        !row.original.is_active 
          ? setConnectModal(true) : connectWhatsapp(row)
      }, 
      icon: <ConnectIcon size={20} />
    },
    { action: () => setDeleteModal(true), icon: <TrashIcon size={20} /> },
  ], []);

  const userInfo = useSelector((state) => state.auth?.user);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [addNewModal, setAddNewModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [connectModal, setConnectModal] = useState(false);
  const [searchEmployee, setSearchEmployee] = useState(null);
  const [totalsEmployee, setTotalsEmployee] = useState(0);
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState();
  const [listEmployeeApi] = useListEmployeeMutation();

  const fetchEmployeeList = async () => {
    setShowLoader(true)
    try {
      const res = await listEmployeeApi({
        page: pagination.pageIndex + 1,
        search: searchEmployee || '',
        is_active: selectedStatusFilter || ''
      }).unwrap();

      if (res?.data) {
        const { results, count } = res.data;
        setEmployeeList(results);
        setTotalsEmployee(count)
      };
    } catch (err) {
      Toast.error(err?.data?.message || err?.message || 'Failed to fetch');
      setEmployeeList([]);
    } finally {
      setShowLoader(false);
    }
  };

  const getEmployeeSession = async (employee_id) => {
    try {
      const response = await getSessionApi({ id: employee_id }).unwrap();
      const session = response.data;

      if (!session || !session?.is_active) return false;
      return true;
    } catch (error) {
      Toast.error(error?.data?.message || error?.message || 'Failed to fetch');
    }
  }

  useEffect(() => {
    fetchEmployeeList();
  }, [pagination, selectedStatusFilter]);

  useEffect(() => {
    if (searchEmployee !== null) {
      const searchDebouncing = setTimeout(() => {
        setPagination({
          ...pagination,
          pageIndex: 0
        })
      }, 1000);

      return () => clearTimeout(searchDebouncing);
    }
  }, [searchEmployee])

  useEffect(() => {
    if (!socket) return;

    const handleConnectSuccess = ({ employeeId, message }) => {
      if (selectedRow?.id !== employeeId) return;

      Toast.success(message);
      setConnectModal(false);
      setShowLoader(false);
      setPagination({
        ...pagination,
        pageIndex: 0
      })

      setSelectedRow(() => null);
      const employeeIdEncrypted = encryptUrlSafe(employeeId);
      redirectInNewTab(`/chat/${employeeIdEncrypted}?auth=true`);
    }

    const handleLogout = ({ employeeId, message }) => {
      Toast.success(message)
      setConnectModal(false);
      setShowLoader(false);
      setPagination({
        ...pagination,
        pageIndex: 0
      })

      if (employeeId) {
          socket.emit('leave-employee-room', { employeeId: employeeId });
      }
    }

    // const handleMessageReceived = ({ employeeId, unread_count }) => {
    //   setEmployeeList((prevList) =>
    //     prevList.map((emp) =>
    //       emp.id === employeeId
    //         ? {
    //             ...emp,
    //             unreplied_count: unread_count,
    //           }
    //         : emp
    //     )
    //   );
    // };

    const handleMessageReceived = () => {
      console.log('message-received event')
      setPagination({
        ...pagination,
        pageIndex: 0
      })
    }

    const handleMismatchWhatsappNumber = ({ employeeId, message }) => {
      Toast.error(message);
      setConnectModal(false);
      setShowLoader(false);
    }

    socket.on('connected', handleConnectSuccess)
    socket.on('logout-success', handleLogout)
    socket.on('message-received', handleMessageReceived)
    socket.on('error', handleMismatchWhatsappNumber);

    return () => {
      socket.off('connected', handleConnectSuccess);
      socket.off('logout-success', handleLogout);
      socket.off('message-received', handleMessageReceived);
      socket.off('error', handleMismatchWhatsappNumber);
  };

  }, [socket, fetchEmployeeList])

  const applySearchFilter = (e) => {
    setSearchEmployee(e.target.value)
  }

  const createOrUpdateQueryString = (key, value) => { 
    const params = new URLSearchParams(location.search);
    if (value === undefined || value === "All") {
      params.delete(key);
    } else {
      params.set(key, value);
    }

    return params.toString();
  }

  const setQueryParams = (params, value) => {
    setSelectedStatusFilter(value)
    const queryString = createOrUpdateQueryString(params, value);
    navigate(`${location.pathname}?${queryString}`);
  }

  return (
    <div className='content-wrapper'>
      <div className="table-container">
        <div className='layout-header d-flex'>
          <h2>Employees</h2>
          <button className='add-new-btn' onClick={() => setAddNewModal(true)}>
            <span>+ </span><span>Add New</span>
          </button>
        </div>
        <div className="row"><hr /></div>
        <div className='layout-subheader'>
          {/* <p>Total Employees {totalsEmployee}</p> */}
          <div className='filter-section'>
            <select className="custom-select" 
              value={selectedStatusFilter}
              onChange={(e) => setQueryParams("statuses", e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Connected</option>
              <option value="false">Disconnected</option>
            </select>

            <input 
              type='text' 
              placeholder='Search employees' 
              className='search-box' 
              onChange={applySearchFilter} 
            />
          </div>
        </div>
        <div className='layout-body'>
          {showLoader && <Loader />}
          <ReactTable
            columns={columns}
            data={employeeList}
            pageCount= {Math.ceil(totalsEmployee / DEFAULT_PAGE_SIZE)}
            paginationState = {{ pagination, setPagination }}
            actionMenu={actionMenu}
            setSelectedRow={setSelectedRow}
            totalRecords={totalsEmployee}
          />
        </div>
      </div>
      
      {/* Add New Employee Modal */}
      {addNewModal && 
        <AddNewEmployee
          companyInfo = {userInfo}
          onClose = {() => setAddNewModal(false)}
          paginationState = {{ pagination, setPagination }}
        />
      }

      {/* Edit Employee Modal */}
      {editModal &&
        <EditEmployee
          companyInfo = {userInfo}
          selectedRow = {selectedRow}
          onClose = {() => setEditModal(false)}
          paginationState = {{ pagination, setPagination }}
        />
      }

      {/* View Employee Modal */}
      {viewModal &&
        <ViewEmployee
          companyInfo = {userInfo}
          selectedRow = {selectedRow}
          onClose = {() => setViewModal(false)}
        />
      }

      {/* Delete Employee Modal*/}
      {deleteModal &&
        <DeleteEmployee
          selectedRow = {selectedRow}
          onClose = {() => setDeleteModal(false)}
          paginationState = {{ pagination, setPagination }}
        />
      } 

      {connectModal &&
        <ConnectEmployee
          selectedRow = {selectedRow}
          onClose = {() => setConnectModal(false)}
        />
      }
    </div>
  );
};

export default EmployeePage;
